import { z } from 'zod';
import { isoDateTime, markdown, mediumText, shortText, slug, uuid } from './primitives.js';
import { assignmentResource } from './content.js';
export const capstoneSpecSection = z.object({
    id: uuid,
    title: z.string(),
    anchor: z.string(),
    bodyMd: z.string(),
    orderIndex: z.number().int(),
});
export const capstoneMilestone = z.object({
    id: uuid,
    title: z.string(),
    bodyMd: z.string(),
    effortEstimate: z.string(),
    orderIndex: z.number().int(),
    /** Learner-owned checkbox state. */
    done: z.boolean(),
});
/**
 * A hint is metadata until revealed: `bodyMd` is null and never leaves the
 * server until the learner asks for it.
 */
export const capstoneHint = z.object({
    id: uuid,
    title: z.string(),
    orderIndex: z.number().int(),
    revealed: z.boolean(),
    bodyMd: z.string().nullable(),
});
export const capstoneApproach = z.object({
    id: uuid,
    title: z.string(),
    orderIndex: z.number().int(),
    revealGated: z.boolean(),
    revealed: z.boolean(),
    bodyMd: z.string().nullable(),
    tradeoffsMd: z.string().nullable(),
});
export const capstoneSelfCheck = z.object({
    id: uuid,
    label: z.string(),
    orderIndex: z.number().int(),
    done: z.boolean(),
});
export const capstoneForLearner = z.object({
    id: uuid,
    courseId: uuid,
    courseSlug: slug,
    courseTitle: z.string(),
    title: z.string(),
    subtitle: z.string(),
    overviewMd: z.string(),
    problemStatementMd: z.string(),
    effortEstimate: z.string(),
    prerequisites: z.string(),
    status: z.enum(['draft', 'published']),
    isPreview: z.boolean(),
    specSections: z.array(capstoneSpecSection),
    milestones: z.array(capstoneMilestone),
    hints: z.array(capstoneHint),
    approaches: z.array(capstoneApproach),
    resources: z.array(assignmentResource.extend({ isStarter: z.boolean() })),
    selfChecks: z.array(capstoneSelfCheck),
    hasReferenceSolution: z.boolean(),
    solutionRevealed: z.boolean(),
    referenceSolutionMd: z.string().nullable(),
    /**
     * Whether the chat panel is available on this page. It is not "a model is
     * attached": the panel retrieves and deep-links into the brief in stub mode
     * too, and whether the assistant can answer in its own words is `mode` from
     * `GET /api/chat/config`.
     */
    chatEnabled: z.boolean(),
    /** Visible from day one unless the course gates it; the reason is shown. */
    locked: z.boolean(),
    lockedReason: z.string().nullable(),
});
// ---------------------------------------------------------------- authoring ---
/**
 * A resource as its author sees it. Two fields sit on top of the learner shape:
 * `isStarter`, which promotes it into the sidebar action, and `fileKey` —
 * carried because a PATCH has to restate the attachment, and the console
 * cannot restate a storage key it was never told. A learner never receives it.
 */
export const capstoneResourceForAuthor = assignmentResource.extend({
    isStarter: z.boolean(),
    fileKey: z.string().nullable(),
});
export const capstoneForAuthor = z.object({
    id: uuid,
    courseId: uuid,
    title: z.string(),
    subtitle: z.string(),
    overviewMd: z.string(),
    problemStatementMd: z.string(),
    effortEstimate: z.string(),
    prerequisites: z.string(),
    referenceSolutionMd: z.string().nullable(),
    status: z.enum(['draft', 'published']),
    hasUnpublishedChanges: z.boolean(),
    publishedAt: isoDateTime.nullable(),
    specSections: z.array(capstoneSpecSection),
    milestones: z.array(capstoneMilestone.omit({ done: true })),
    hints: z.array(z.object({
        id: uuid,
        title: z.string(),
        bodyMd: z.string(),
        orderIndex: z.number().int(),
    })),
    approaches: z.array(z.object({
        id: uuid,
        title: z.string(),
        bodyMd: z.string(),
        tradeoffsMd: z.string(),
        revealGated: z.boolean(),
        orderIndex: z.number().int(),
    })),
    resources: z.array(capstoneResourceForAuthor),
    selfChecks: z.array(capstoneSelfCheck.omit({ done: true })),
    authors: z.array(z.object({ userId: uuid, name: z.string(), email: z.string() })),
});
export const upsertCapstoneRequest = z.object({
    title: shortText.min(1),
    subtitle: mediumText.default(''),
    overviewMd: markdown.default(''),
    problemStatementMd: markdown.default(''),
    effortEstimate: shortText.default(''),
    prerequisites: shortText.default(''),
    referenceSolutionMd: markdown.nullable().default(null),
});
/** One request shape for every ordered block type; `kind` picks the table. */
export const capstoneBlockKind = z.enum([
    'spec_section',
    'milestone',
    'hint',
    'approach',
    'self_check',
]);
export const upsertCapstoneBlockRequest = z.object({
    title: shortText.default(''),
    bodyMd: markdown.default(''),
    tradeoffsMd: markdown.default(''),
    effortEstimate: shortText.default(''),
    label: shortText.default(''),
    revealGated: z.boolean().default(true),
    anchor: z.string().max(96).optional(),
});
export const revealRequest = z.object({
    targetType: z.enum([
        'capstone_hint',
        'capstone_approach',
        'capstone_solution',
        'assignment_solution',
    ]),
    targetId: uuid,
    /** The reference solution requires an explicit typed confirmation. */
    confirm: z.boolean().default(false),
});
export const revealResponse = z.object({
    targetType: z.string(),
    targetId: uuid,
    bodyMd: z.string().nullable(),
    tradeoffsMd: z.string().nullable(),
    revealedAt: isoDateTime,
});
export const checklistToggleRequest = z.object({
    itemType: z.enum(['assignment', 'capstone_milestone', 'capstone_self_check']),
    itemId: uuid,
    done: z.boolean(),
});
export const assignCapstoneAuthorsRequest = z.object({
    userIds: z.array(uuid),
});
//# sourceMappingURL=capstone.js.map